import classNames from "classnames";
import { FC, useRef } from "react";
import { Link } from "react-router-dom";
import { usePermissionsContext } from "../context/permissionContext";
import useOnClickOutside from "../hooks/useOnclickOutside";
import useToggle from "../hooks/useToggle";
import { useI18n } from "../hooks/useTranslations";
import Hamburger from "./Hamburger";
import styles from "./MenuButton.module.css";
import SvgBlock from "./icons/Block";
import SvgBoxInSquare from "./icons/BoxInSquare";
import SvgCostCenter from "./icons/CostCenter";
import SvgHelmet from "./icons/Helmet";
import SvgItem from "./icons/Item";
import SvgLabel from "./icons/Label";
import SvgPackaging from "./icons/Packaging";
import SvgPending from "./icons/Pending";
import SvgPriority from "./icons/Priority";
import SvgRequest from "./icons/Request";
import SvgSupplier from "./icons/Supplier";
import SvgType from "./icons/Type";
import SvgUser from "./icons/User";
import Icon from "./ui/Icon";

const MenuButton: FC = () => {
  const i18n = useI18n();
  const { close, isOpen, toggle } = useToggle();
  const permissions = usePermissionsContext();

  const menuRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(menuRef, () => close());

  const menuItems = permissions.hasAccess
    ? [
        ...[
          {
            to: "/requests",
            title: i18n.t.common.requests,
            icon: <Icon glyph={SvgRequest} className={styles.icon} />,
            subRoutes: [
              {
                to: "/requests",
                title: i18n.t.common.allRequests,
                icon: <Icon glyph={SvgRequest} className={styles.icon} />,
              },
              {
                to: "/requests/on_going",
                title: i18n.t.common.onGoing,
                icon: <Icon glyph={SvgPending} className={styles.icon} />,
              },
              {
                to: "/requests/closed_and_discarded",
                title: i18n.t.common.closedAndDiscarded,
                icon: <Icon glyph={SvgBlock} className={styles.icon} />,
              },
              {
                to: "/requests/grouped_by_items",
                title: i18n.t.common.groupedByItems,
                icon: <Icon glyph={SvgBoxInSquare} className={styles.icon} />,
              },
              {
                to: "/requests/helmets",
                title: i18n.t.common.helmets,
                icon: <Icon glyph={SvgHelmet} className={styles.icon} />,
              },
            ],
          },
          { to: "/items", title: i18n.t.items.plural, icon: <Icon glyph={SvgItem} className={styles.icon} /> },
        ],

        ...(permissions.isWarehouseOperator
          ? [
              {
                to: "/suppliers",
                title: i18n.t.suppliers.plural,
                icon: <Icon glyph={SvgSupplier} className={styles.icon} />,
              },
              {
                to: "/labels",
                title: i18n.t.labels.plural,
                icon: <Icon glyph={SvgLabel} className={styles.icon} />,
              },
              {
                to: "/packagings",
                title: i18n.t.packagings.plural,
                icon: <Icon glyph={SvgPackaging} className={styles.icon} />,
              },
              { to: "/types", title: i18n.t.common.types, icon: <Icon glyph={SvgType} className={styles.icon} /> },
              {
                to: "/priorities",
                title: i18n.t.common.priorities,
                icon: <Icon glyph={SvgPriority} className={styles.icon} />,
              },
              {
                to: "/cost_centers",
                title: i18n.t.costCenters.plural,
                icon: <Icon glyph={SvgCostCenter} className={styles.icon} />,
              },
            ]
          : []),
        ...(permissions.hasUserMananagement
          ? [{ to: "/users", title: i18n.t.users.plural, icon: <Icon glyph={SvgUser} className={styles.icon} /> }]
          : []),
      ]
    : [];

  return (
    <div ref={menuRef}>
      <Hamburger onClick={toggle} isOpen={isOpen} isDisabled={!permissions.hasAccess}></Hamburger>
      <div className={classNames(styles.menu, isOpen ? styles.visible : undefined)} onClick={close}>
        <ul className={styles.list}>
          {menuItems.map((m) => (
            <li key={`_li_${m.to}`} className={styles["menu-wrapper"]}>
              <Link to={m.to} className={styles["menu-item"]}>
                {m.icon}
                {m.title}
              </Link>

              {m.subRoutes && (
                <ul className={styles["submenu"]}>
                  {m.subRoutes.map((sub) => (
                    <li key={sub.to}>
                      <Link to={sub.to} className={styles["menu-item"]}>
                        {sub.icon}
                        {sub.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default MenuButton;
